import type { PublicModel } from '@aitoken/contracts';
import {
  chargeForTokens,
  currencySymbol,
  maxReserveMicros,
  maxTokens,
  microsToString,
} from '@aitoken/contracts';

/**
 * 首页演示用的金额：全部由本站真实价格规则算出，保证“冻结 − 结算 = 退回”对得上。
 * 场景：720p · 16:9 · 时长交给模型自动决定（按 15 秒上界冻结），实际输出 8 秒。
 */
export const DEMO = {
  taskId: 'task_01a18c6a3f197896b3e61f7f40fa4858',
  requestId: 'req_7f3b9c2e41d84a06',
  idempotencyKey: 'order-A1001-v1',
  resolution: '720p',
  ratio: '16:9',
  actualSeconds: 8,
  /** 起始可用额度（演示账户） */
  startBalance: 500_000_000n,
} as const;

export interface DemoQuote {
  symbol: string;
  currency: 'CNY' | 'USD';
  reserve: string;
  charged: string;
  released: string;
  tokens: number;
  maxTokens: number;
  afterReserve: string;
  afterRelease: string;
  start: string;
}

/** 价格规则未加载时的回落值（与种子数据一致：720p 每百万 token ¥60）。 */
const FALLBACK: DemoQuote = {
  symbol: '¥',
  currency: 'CNY',
  reserve: '19.440000',
  charged: '10.370000',
  released: '9.070000',
  tokens: 172_800,
  maxTokens: 324_000,
  afterReserve: '480.560000',
  afterRelease: '489.630000',
  start: '500.000000',
};

export function demoQuote(model: PublicModel | undefined): DemoQuote {
  if (!model) return FALLBACK;
  try {
    const caps = model.capabilities;
    const rules = model.pricing.rules;
    const q = {
      resolution: DEMO.resolution,
      ratio: DEMO.ratio,
      duration: -1,
      inputVideoSeconds: 0,
      hasVideoInput: false,
    };
    const reserve = maxReserveMicros(caps, rules, q);
    const tokens = maxTokens(caps, { ...q, duration: DEMO.actualSeconds });
    const charged = chargeForTokens(rules, DEMO.resolution, false, tokens);
    const released = reserve - charged;
    return {
      symbol: currencySymbol(model.pricing.currency),
      currency: model.pricing.currency,
      reserve: microsToString(reserve),
      charged: microsToString(charged),
      released: microsToString(released),
      tokens,
      maxTokens: maxTokens(caps, q),
      afterReserve: microsToString(DEMO.startBalance - reserve),
      afterRelease: microsToString(DEMO.startBalance - charged),
      start: microsToString(DEMO.startBalance),
    };
  } catch {
    return FALLBACK;
  }
}

/** "19.440000" → "19.44" */
export function yuan(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : v;
}

/** 某分辨率 5 秒 16:9 文生视频的冻结额（= 实际费用，时长固定时两者相同）。 */
export function fiveSecondCost(model: PublicModel, resolution: string, hasVideoInput = false): string | null {
  try {
    const caps = model.capabilities;
    const ratio = caps.ratios.includes('16:9') ? '16:9' : (caps.ratios[0] ?? '16:9');
    return microsToString(
      maxReserveMicros(caps, model.pricing.rules, {
        resolution,
        ratio,
        duration: 5,
        inputVideoSeconds: 0,
        hasVideoInput,
      }),
    );
  } catch {
    return null;
  }
}
